import { useCallback, useRef, useState } from 'react';

import type { ITanStackTableProps } from '@feathr/components';
import { removeEmpty } from '@feathr/hooks';
import type { Attributes, Collection, IBaseAttributes, IListParams, Model } from '@feathr/rachis';

/**
 * The controlled sort state for a `TanStackTable`. Derived from `TanStackTable`'s own props rather
 * than imported by name: `@feathr/components` exports the *legacy* `Table`'s `ISorted` under that
 * name (its `desc` is the narrower `true`), so importing `ISorted` from the barrel gets the wrong
 * type.
 *
 * Deliberately not generic over the row type. `TanStackTable`'s `sort` is a plain
 * `Array<{ id: string; desc?: boolean }>` — a column id is not necessarily a model attribute (it
 * can be a computed or nested field) — so a row type parameter here would only look like it
 * validated the ids.
 */
export type TCollectionTableSort = NonNullable<ITanStackTableProps<unknown>['sort']>;

export interface IUseCollectionTableOptions<T extends Model<IBaseAttributes>> {
  /**
   * List params merged into every request — `filters`, `keywords`, `only`, `exclude`. The table
   * returns to the first page whenever they change, so rows are never stranded on a page the new
   * result set no longer has. `ordering` and `pagination` are owned by the hook.
   */
  readonly defaultParams?: Omit<IListParams<Attributes<T>>, 'ordering' | 'pagination'>;
  /**
   * Page the table starts on. Defaults to 0; pass a restored value to resume where the user left
   * off. The page-reset rule below does not fire on mount, so a restored page survives the first
   * render.
   */
  readonly initialPage?: number;
  /**
   * Page size the table starts on (legacy `<Table initialPagesize>`). Use one of the sizes the
   * pagination dropdown offers (10/20/50/100) or the dropdown renders blank.
   */
  readonly initialPageSize?: number;
  /** The default column ordering, matching the legacy `<Table initialSort>`. */
  readonly initialSort?: TCollectionTableSort;
}

export interface IUseCollectionTableReturn<T extends Model<IBaseAttributes>> {
  readonly idKey: 'id';
  readonly isLoading: boolean;
  readonly items: T[];
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (pageSize: number, page: number) => void;
  readonly onSortChange: (sort: TCollectionTableSort) => void;
  readonly page: number;
  readonly pages: number;
  readonly pageSize: number;
  readonly sort: TCollectionTableSort;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Wires the server-side sort, pagination, and page-size state of a `TanStackTable` to a rachis
 * `Collection`, issues the matching `collection.list()`, and returns the table's props. Spread the
 * return value onto `<TanStackTable>` and supply `columns` yourself:
 *
 * ```tsx
 * const tableProps = useCollectionTable(Users, {
 *   defaultParams: { filters },
 *   initialSort: [{ id: 'last_name' }],
 * });
 *
 * return <TanStackTable<User> {...tableProps} columns={columns} />;
 * ```
 *
 * `TanStackTable` is fully controlled (`manualPagination` / `manualSorting`), so this hook owns
 * `page`, `pageSize`, and `sort` — the work the legacy `<Table collection>` did internally — and
 * translates the sort into the backend's `ordering` param.
 *
 * The hook reads MobX observables off the list response (`models`, `isPending`, `pagination`), so
 * the component that calls it **must** be wrapped in `observer`. Without it the table never leaves
 * its loading state.
 */
export function useCollectionTable<T extends Model<IBaseAttributes>>(
  collection: Collection<T>,
  {
    defaultParams,
    initialPage = 0,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialSort = [],
  }: IUseCollectionTableOptions<T> = {},
): IUseCollectionTableReturn<T> {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<TCollectionTableSort>(initialSort);

  /*
   * Narrowing the filters usually shrinks the result set, so return to the first page — parity with
   * the legacy `<Table>`, which reset the page on every filter change.
   *
   * Compared by value, not identity: consumers assemble `defaultParams` inline, so the object is a
   * new one on every render. And adjusted during render rather than in an effect, because
   * `collection.list()` below runs in the render body — an effect would let a request go out for
   * the page we are about to leave before the reset landed. Seeding the ref with the first
   * `paramsKey` also means mount is not treated as a change, so an `initialPage` survives it.
   */
  const paramsKey = JSON.stringify(defaultParams ?? {});
  const previousParamsKey = useRef(paramsKey);
  let currentPage = page;
  if (previousParamsKey.current !== paramsKey) {
    previousParamsKey.current = paramsKey;
    currentPage = 0;
    setPage(0);
  }

  const onPageChange = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);

  const onPageSizeChange = useCallback((newPageSize: number, newPage: number): void => {
    setPageSize(newPageSize);
    setPage(newPage);
  }, []);

  // Re-ordering the whole result set makes the current page arbitrary, so drop back to the first.
  const onSortChange = useCallback((newSort: TCollectionTableSort): void => {
    setSort(newSort);
    setPage(0);
  }, []);

  const params: IListParams<Attributes<T>> = {
    ...defaultParams,
    pagination: { page: currentPage, page_size: pageSize },
  };
  if (sort.length) {
    params.ordering = sort.map((rule) => `${rule.desc ? '-' : ''}${rule.id}`);
  }
  if (params.filters) {
    /*
     * `Collection.list` drops empty *top-level* params but not empty entries inside `filters`, and
     * an undefined filter value serializes to the literal string "undefined" in the query.
     */
    params.filters = removeEmpty(params.filters);
  }

  const list = collection.list(params);

  return {
    idKey: 'id',
    isLoading: list.isPending,
    // `models` is an observable array; TanStack compares `data` by reference, so hand over a copy.
    items: list.models.slice(),
    onPageChange,
    onPageSizeChange,
    onSortChange,
    page: currentPage,
    /*
     * `pages` comes straight off the response meta, which the backend derives from the total count.
     * A pending fetch zeroes it, but `TanStackTable` swaps the whole table — pagination included —
     * for its loading state while `isLoading`, so the zero never reaches the screen.
     */
    pages: list.pagination.pages,
    pageSize,
    sort,
  };
}

export default useCollectionTable;
